import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { setUnauthorizedHandler } from "@/api/client";

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: () => import("@/pages/Login.vue"),
    meta: { public: true },
  },
  {
    path: "/",
    component: () => import("@/layouts/AppLayout.vue"),
    children: [
      {
        path: "",
        name: "overview",
        component: () => import("@/pages/Overview.vue"),
      },
      {
        path: "ob-advisories",
        name: "ob-advisories",
        meta: { superAdminOnly: true },
        component: () => import("@/pages/OBAdvisories.vue"),
      },
      {
        path: "active-trades",
        name: "active-trades",
        component: () => import("@/pages/ActiveTrades.vue"),
      },
      {
        path: "strategies",
        name: "strategies",
        component: () => import("@/pages/Strategies.vue"),
      },
      {
        path: "performance",
        name: "performance",
        meta: { superAdminOnly: true },
        component: () => import("@/pages/Performance.vue"),
      },
      {
        path: "research",
        name: "research",
        component: () => import("@/pages/Research.vue"),
      },
      {
        path: "webhooks",
        name: "webhooks",
        component: () => import("@/pages/Webhooks.vue"),
      },
      {
        path: "options-builder",
        name: "options-builder",
        component: () => import("@/pages/OptionsBuilder.vue"),
      },
      {
        path: "trade-pods",
        name: "trade-pods",
        component: () => import("@/pages/Containers.vue"),
      },
      {
        path: "containers",
        redirect: { name: "trade-pods" },
      },
      {
        path: "executions",
        name: "executions",
        component: () => import("@/pages/Executions.vue"),
      },
      {
        path: "management",
        name: "management",
        meta: { superAdminOnly: true },
        component: () => import("@/pages/Management.vue"),
      },
      {
        path: "api-docs",
        name: "api-docs",
        meta: { superAdminOnly: true },
        component: () => import("@/pages/ApiDocs.vue"),
      },
      {
        path: "logs",
        name: "logs",
        meta: { superAdminOnly: true },
        component: () => import("@/pages/Logs.vue"),
      },
      {
        path: "dhan-token",
        name: "dhan-token",
        meta: { superAdminOnly: true },
        component: () => import("@/pages/DhanToken.vue"),
      },
      {
        path: "telegram-bots",
        name: "telegram-bots",
        meta: { superAdminOnly: true },
        component: () => import("@/pages/TelegramBots.vue"),
      },
      {
        path: "model-portfolios",
        name: "model-portfolios",
        meta: { superAdminOnly: true },
        component: () => import("@/pages/ModelPortfolios.vue"),
      },
      {
        path: "model-portfolios/create",
        name: "portfolio-create",
        meta: { superAdminOnly: true },
        component: () => import("@/pages/PortfolioFormPage.vue"),
      },
    ],
  },
  {
    path: "/model-portfolios/:id",
    component: () => import("@/layouts/PortfolioLayout.vue"),
    meta: { superAdminOnly: true },
    children: [
      {
        path: "performance",
        name: "portfolio-performance",
        component: () => import("@/pages/PortfolioPerformance.vue"),
      },
      {
        path: "rebalance",
        name: "portfolio-rebalance",
        component: () => import("@/pages/PortfolioRebalance.vue"),
      },
      {
        path: "edit",
        name: "portfolio-edit",
        component: () => import("@/pages/PortfolioFormPage.vue"),
      },
    ],
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  if (to.meta.public) {
    if (to.name === "login") {
      const ok = await auth.verify();
      if (ok) return { name: "overview" };
    }
    return true;
  }

  const ok = await auth.verify();
  if (!ok) {
    return { name: "login", query: { redirect: to.fullPath } };
  }

  if (!auth.isSuperAdmin && to.matched.some((r) => r.meta.superAdminOnly)) {
    return { name: "overview" };
  }

  return true;
});

setUnauthorizedHandler(() => {
  useAuthStore().clearSession();
  const current = router.currentRoute.value;
  if (current.meta.public) return;
  void router.replace({ name: "login", query: { redirect: current.fullPath } });
});
